import type { SVGProps } from 'react';

export type IconName =
  | 'droplet'
  | 'gear'
  | 'thread'
  | 'palette'
  | 'rig'
  | 'mountain'
  | 'shield'
  | 'globe'
  | 'handshake'
  | 'badge'
  | 'search'
  | 'download'
  | 'table'
  | 'mail'
  | 'phone'
  | 'pin'
  | 'arrow'
  | 'flask'
  | 'check';

const paths: Record<IconName, JSX.Element> = {
  droplet: <path d="M12 3s6 6.5 6 11a6 6 0 1 1-12 0c0-4.5 6-11 6-11Z" />,
  gear: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.5v3M12 18.5v3M21.5 12h-3M5.5 12h-3M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1M18.7 18.7l-2.1-2.1M7.4 7.4 5.3 5.3" />
    </>
  ),
  thread: (
    <>
      <path d="M4 6c4 0 4 12 8 12s4-12 8-12" />
      <circle cx="4" cy="6" r="1.4" />
      <circle cx="20" cy="6" r="1.4" />
    </>
  ),
  palette: (
    <>
      <path d="M12 3a9 9 0 1 0 0 18c1.2 0 2-.9 2-2 0-1-.8-1.6-.8-2.6 0-.8.7-1.4 1.6-1.4H16a5 5 0 0 0 5-5c0-4.2-4-7-9-7Z" />
      <circle cx="7.5" cy="10.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="7.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="16.5" cy="10.5" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  rig: (
    <>
      <path d="M6 21 12 3l6 18M8.5 14h7M4 21h16" />
    </>
  ),
  mountain: <path d="M3 20 10 7l4 6 2-3 5 10H3Z" />,
  shield: (
    <>
      <path d="M12 3 5 6v5c0 4.4 3 8.3 7 9.5 4-1.2 7-5.1 7-9.5V6l-7-3Z" />
      <path d="m9.2 12 2 2 3.6-3.8" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.5 3.8 5.7 3.8 9S14.5 18.5 12 21c-2.5-2.5-3.8-5.7-3.8-9S9.5 5.5 12 3Z" />
    </>
  ),
  handshake: <path d="m8 13 2.5 2.5c.6.6 1.6.6 2.2 0L14 14l3 3M3 10l4-4 4 3 3-2 4 3.5M3 10l3 3M21 10.5 18 13" />,
  badge: (
    <>
      <circle cx="12" cy="9" r="5.5" />
      <path d="m8.5 13.5-1.5 7 5-2.5 5 2.5-1.5-7" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  download: <path d="M12 3v12m0 0 4-4m-4 4-4-4M4 19h16" />,
  table: (
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="1.5" />
      <path d="M3.5 9.5h17M3.5 14.5h17M9 4.5v15" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m4 7 8 6 8-6" />
    </>
  ),
  phone: (
    <path d="M6.5 3.5 9 4l1 3.5-1.8 1.4a12 12 0 0 0 5.4 5.4L15 16.5 18.5 18l.5 2.5c-9 1.5-16.5-6-15-15Z" />
  ),
  pin: (
    <>
      <path d="M12 21s7-6 7-11a7 7 0 1 0-14 0c0 5 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  arrow: <path d="M4 12h15m0 0-6-6m6 6-6 6" />,
  flask: <path d="M9 3h6M10 3v6l-5 8.5A2 2 0 0 0 6.7 21h10.6a2 2 0 0 0 1.7-3.5L14 9V3M7.5 15h9" />,
  check: <path d="m5 12 4.5 4.5L19 7" />,
};

interface IconProps extends SVGProps<SVGSVGElement> {
  name: IconName;
}

export default function Icon({ name, className = 'h-6 w-6', ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {paths[name] ?? paths.droplet}
    </svg>
  );
}
